import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import Loading from 'components/loading/loading.component'
import ExploreMoreCard from 'components/explore-more-card/explore-more-card.component'
import TakeNextStepCard, { TakeNextStepCTA } from 'components/take-next-step-card/take-next-step-card.component'
import SortableCareerCard from 'components/sortable/sortable-career-card.component'
import SortableDiscardRow from 'components/sortable/sortable-discard-row.component'
import DroppableArea from 'components/sortable/droppable-area.component'
import CareerItem from 'components/career-item/career-item.component'
import CareerCardPlaceholder from 'components/career-item/career-card-placeholder.component'
import { noPreviewImage } from 'utills/image'
import { uniformCardWidth } from 'utills/card-width'
import { useLayout } from 'contexts/LayoutContext'
import { useCareersReorder } from 'hooks/useCareersReorder'
import { CareerTypeEnum } from 'enums/glimps.enums'
import {
  getEndUserCareer,
  getPreviewSideData,
} from 'services/deeper-dive.service'
import { getCareerSelectionList, CareerSelectionItem } from 'services/on-board'

const UserCareersScreen: React.FC = () => {
  const navigate = useNavigate()
  const {
    careersReorderMode,
    setCareersReorderMode,
    careersSaveTrigger,
    setIsChatOpen,
  } = useLayout()

  // Reset reorder mode when leaving page
  useEffect(() => {
    return () => {
      setCareersReorderMode(false)
    }
  }, [setCareersReorderMode])

  const {
    data: careers = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['userCareersPreview'],
    queryFn: () => getEndUserCareer(CareerTypeEnum.preview),
    retry: 0,
    refetchOnWindowFocus: false,
  })

  const { data: previewData } = useQuery({
    queryKey: ['previewSideData'],
    queryFn: getPreviewSideData,
    retry: 0,
    refetchOnWindowFocus: false,
  })

  const { data: selectionList = [] } = useQuery({
    queryKey: ['careerSelectionList'],
    queryFn: getCareerSelectionList,
    retry: 0,
    refetchOnWindowFocus: false,
  })

  const nextBatchAvatars = useMemo(() => {
    const eligible = selectionList.filter(
      (c: CareerSelectionItem) =>
        !c.is_unlocked && c.status === 'published',
    )
    const shuffled = [...eligible].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, 5).map((c: CareerSelectionItem) => c.icon_url)
  }, [selectionList])

  const reorder = useCareersReorder({
    serverCareers: careers,
    reorderMode: careersReorderMode,
    setReorderMode: setCareersReorderMode,
    saveTrigger: careersSaveTrigger,
  })

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const goToCareer = (id: string) => {
    const item = careers.find(c => c.id === id)
    if (!item) return
    if (item.is_completed) {
      navigate(`/user-careers/careers/career-insights/${item.career_id}`)
    } else {
      navigate(`/user-careers/deeper-dive/${item.id}`, {
        state: {
          career_id: item.id,
          cover_video_id: item.cover_video_id,
          deepdive_level: item.deepdive_level,
        },
      })
    }
  }

  const handleUnlockNext = () => {
    const availability = previewData?.is_available_careers
    if (availability === 'continue') {
      navigate('/user-careers/unlock-next/start')
      return
    }
    if (availability === 'unlock') {
      navigate('/user-careers/unlock-next/select')
      return
    }
    // Fallback: if BE didn't flag availability but locked published careers
    // still exist in selection list, route user to selection.
    const lockedCount = selectionList.filter(
      (c: CareerSelectionItem) =>
        !c.is_unlocked && c.status === 'published',
    ).length
    if (lockedCount > 0) {
      navigate('/user-careers/unlock-next/select')
    }
  }

  if (isLoading) return <Loading />
  if (isError) {
    return (
      <div className="p-6 text-sm text-destructive">
        Failed to load careers. Please refresh and try again.
      </div>
    )
  }

  const { top3, watchList, discard } = reorder
  const top3CardWidth = uniformCardWidth(top3.map(c => c.name))
  const watchListCardWidth = uniformCardWidth(watchList.map(c => c.name))

  // Surface careers the user has NOT finished a deeper dive on yet. A career
  // is "done" when is_completed=true (closer-look finished). Order priority:
  // top 3 → watch list → discard → fallback CTAs (Sherpa / more videos).
  const nextStepData = (() => {
    const incompleteTop = top3.filter(c => !c.is_completed)
    if (incompleteTop.length > 0) {
      return {
        items: incompleteTop,
        subtitle: 'Go deeper on your top 3 with more insights from experts.',
      }
    }
    const incompleteWatch = watchList.filter(c => !c.is_completed)
    if (incompleteWatch.length > 0) {
      return {
        items: incompleteWatch.slice(0, 3),
        subtitle:
          'Top 3 done — keep momentum with a deeper dive from your watch list.',
      }
    }
    const incompleteDiscard = discard.filter(c => !c.is_completed)
    if (incompleteDiscard.length > 0) {
      return {
        items: incompleteDiscard.slice(0, 3),
        subtitle:
          'Curious about something you set aside? Take a deeper look at a discarded career.',
      }
    }
    return { items: [] as typeof top3, subtitle: '' }
  })()

  const nextStepEmptyCTAs = (() => {
    if (nextStepData.items.length !== 0) return undefined
    const ctas: TakeNextStepCTA[] = [
      {
        label: 'Explore with Career Sherpa',
        description: 'Talk through what comes next with your AI guide.',
        onClick: () => setIsChatOpen(true),
      },
    ]
    // Only surface the unlock CTA when there is actually something to unlock,
    // otherwise the button is a no-op (mirrors handleUnlockNext guards).
    const availability = previewData?.is_available_careers
    const lockedPublishedCount = selectionList.filter(
      (c: CareerSelectionItem) =>
        !c.is_unlocked && c.status === 'published',
    ).length
    const canUnlock =
      availability === 'continue' ||
      availability === 'unlock' ||
      lockedPublishedCount > 0
    if (canUnlock) {
      ctas.push({
        label: 'Watch more preview videos',
        description: 'Open up the next batch of careers to explore.',
        onClick: handleUnlockNext,
      })
    }
    return ctas
  })()

  const content = (
    <div
      className={`${
        careersReorderMode ? 'w-full' : 'max-w-[1179px]'
      } w-full mx-auto px-0 pt-6 pb-6 flex gap-8 items-start`}
    >
      <div className="flex-1 min-w-0 flex flex-col gap-6">
        <Section
          title="Top 3"
          subtitle="Highest potential for my future"
          minHeight={224}
          actionLabel={
            !careersReorderMode && top3.length === 0 ? 'Find your favorites' : undefined
          }
          onAction={handleUnlockNext}
        >
          <SortableContext
            id="top_three"
            items={top3.map(c => c.id)}
            strategy={rectSortingStrategy}
            disabled={!careersReorderMode}
          >
            <DroppableArea
              id="top_three"
              enabled={careersReorderMode}
              isEmpty={top3.length === 0}
              emptyLabel="Drop a career into Top 3"
              className={`flex gap-4 items-start ${
                careersReorderMode ? 'min-h-[180px] p-3' : 'min-h-[150px]'
              }`}
            >
              {top3.map((c, i) => (
                <SortableCareerCard
                  key={c.id}
                  item={c}
                  rank={i + 1}
                  reorderMode={careersReorderMode}
                  onClick={() => goToCareer(c.id)}
                  containerWidth={top3CardWidth}
                />
              ))}
              {!careersReorderMode &&
                Array.from({ length: Math.max(0, 3 - top3.length) }).map((_, i) => (
                  <CareerCardPlaceholder key={`top3-empty-${i}`} width={top3CardWidth} />
                ))}
            </DroppableArea>
          </SortableContext>
        </Section>

        <Divider />

        <Section
          title="Watch List"
          subtitle="Have some potential for my future"
          minHeight={224}
          actionLabel={
            !careersReorderMode && watchList.length === 0 ? 'Build your list' : undefined
          }
          onAction={handleUnlockNext}
        >
          <SortableContext
            id="watch_list"
            items={watchList.map(c => c.id)}
            strategy={rectSortingStrategy}
            disabled={!careersReorderMode}
          >
            <DroppableArea
              id="watch_list"
              enabled={careersReorderMode}
              isEmpty={watchList.length === 0}
              emptyLabel="Drop a career into Watch List"
              className={`flex flex-wrap gap-4 items-start ${
                careersReorderMode ? 'min-h-[180px] p-3' : 'min-h-[150px]'
              }`}
            >
              {watchList.map((c, i) => (
                <SortableCareerCard
                  key={c.id}
                  item={c}
                  rank={top3.length + i + 1}
                  reorderMode={careersReorderMode}
                  onClick={() => goToCareer(c.id)}
                  containerWidth={watchListCardWidth}
                />
              ))}
              {!careersReorderMode &&
                Array.from({ length: Math.max(0, 6 - watchList.length) }).map((_, i) => (
                  <CareerCardPlaceholder key={`watch-empty-${i}`} width={watchListCardWidth} />
                ))}
            </DroppableArea>
          </SortableContext>
        </Section>

        <Divider />

        <Section title="Discard" subtitle="Not in my future" minHeight={308}>
          <SortableContext
            id="discard"
            items={discard.map(c => c.id)}
            strategy={rectSortingStrategy}
            disabled={!careersReorderMode}
          >
            <DroppableArea
              id="discard"
              enabled={careersReorderMode}
              isEmpty={discard.length === 0}
              emptyLabel="Drop a career into Discard"
              className={(() => {
                if (careersReorderMode) {
                  // Match the populated non-reorder section's max height so
                  // the section doesn't collapse as cards are dragged out.
                  // `auto-rows-min` + `content-start` keeps each row at its
                  // natural height instead of stretching to fill the min-h.
                  return discard.length === 0
                    ? 'flex flex-wrap gap-4 p-3 min-h-[260px] content-start'
                    : 'grid grid-cols-2 gap-x-4 gap-y-3 p-3 min-h-[260px] auto-rows-min content-start'
                }
                return discard.length === 0
                  ? 'flex flex-wrap gap-4 pt-3 min-h-[150px]'
                  : 'grid grid-cols-2 gap-x-4 gap-y-3 pt-3 min-h-[80px] auto-rows-min content-start'
              })()}
            >
              {discard.length === 0
                ? !careersReorderMode &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <CareerCardPlaceholder key={`discard-empty-${i}`} />
                  ))
                : discard.map(c => (
                    <SortableDiscardRow
                      key={c.id}
                      item={c}
                      reorderMode={careersReorderMode}
                      onClick={() => goToCareer(c.id)}
                    />
                  ))}
            </DroppableArea>
          </SortableContext>
        </Section>
      </div>

      {!careersReorderMode && (
        <div className="shrink-0 flex flex-col gap-6 w-[320px]">
          <ExploreMoreCard avatars={nextBatchAvatars} onContinue={handleUnlockNext} />
          <TakeNextStepCard
            items={nextStepData.items}
            subtitle={nextStepData.subtitle}
            onSelect={goToCareer}
            emptyCTAs={nextStepEmptyCTAs}
            emptyTitle="You've covered the basics"
            emptySubtitle="Pick a path to keep building your career picture."
          />
        </div>
      )}
    </div>
  )

  if (!careersReorderMode) return content

  const active = reorder.activeItem
  const activeIsDiscardRow = active?.category === 'discard'
  const activeCardWidth =
    active?.category === 'watch_list' ? watchListCardWidth : top3CardWidth

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={args => {
        const pointer = pointerWithin(args)
        return pointer.length > 0 ? pointer : rectIntersection(args)
      }}
      onDragStart={reorder.onDragStart}
      onDragOver={reorder.onDragOver}
      onDragEnd={reorder.onDragEnd}
      onDragCancel={reorder.onDragCancel}
    >
      {content}
      <DragOverlay dropAnimation={null}>
        {active ? (
          activeIsDiscardRow ? (
            <div
              className="flex items-center gap-2 border border-gray-light rounded-md px-3 py-2 bg-white shadow-lg cursor-grabbing"
              style={{ width: 460 }}
            >
              <img
                src={active.icon_url || noPreviewImage}
                alt={active.name}
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
              <span className="font-sans text-sm font-semibold text-brand truncate">
                {active.name}
              </span>
            </div>
          ) : (
            <div className="cursor-grabbing">
              <CareerItem
                id={active.id}
                name={active.name}
                icon_url={active.icon_url || noPreviewImage}
                rank={0}
                showRanking={false}
                showStatus={false}
                pressed={() => undefined}
                containerHeight={150}
                containerWidth={activeCardWidth}
                height={72}
                width={72}
                fontSize="body-sm-emphasis"
              />
            </div>
          )
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

const Section: React.FC<{
  title: string
  subtitle: string
  minHeight: number
  actionLabel?: string
  onAction?: () => void
  children: React.ReactNode
}> = ({ title, subtitle, minHeight, actionLabel, onAction, children }) => (
  <section className="flex flex-col gap-4" style={{ minHeight }}>
    <div className="flex items-start justify-between" style={{ height: 44 }}>
      <div className="flex flex-col gap-1">
        <h2 className="font-sans text-lg font-semibold leading-4 tracking-normal text-brand">
          {title}
        </h2>
        <p className="font-sans text-xs font-normal leading-4 tracking-normal text-[#303030]">
          {subtitle}
        </p>
      </div>
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          className="font-sans text-sm font-semibold leading-5 tracking-normal text-right text-green hover:underline"
        >
          {actionLabel}
        </button>
      )}
    </div>
    {children}
  </section>
)

const Divider: React.FC = () => (
  <div
    style={{ height: 0, borderTop: '1px solid #E8E8E8', borderRadius: 2 }}
  />
)

export default UserCareersScreen
